import { Pressable, RowButton } from "@/components/ui/pressable";
import { useEffect, useState, type KeyboardEvent, type ReactNode } from "react";
import { formatDistanceToNow } from "date-fns";
import { useGetList } from "ra-core";
import { Link } from "react-router";
import { ArrowUpRight, Plus, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { IconButton } from "@/components/ui/icon-button";
import { Avatar } from "@/components/atomic-crm/contacts/Avatar";
import { CompanyAvatar } from "@/components/atomic-crm/companies/CompanyAvatar";
import type { Company, Contact } from "@/components/atomic-crm/types";
import { getClientShowPath, getContactShowPath } from "@/app/routing";
import { getContactPrimaryEmail } from "@/modules/billing/billingRecipientResolution";
import { ClientQuickActions } from "@/modules/clients/ClientQuickActions";
import { isLeadLifecycleStatus } from "@/modules/constants/contactStatus";
import { ContactQuickActions } from "@/modules/contacts/ContactQuickActions";
import { ContactPortalAccessSection } from "@/modules/portal/ContactPortalAccessSection";
import {
  RelatedSection,
  useInlineSave,
} from "@/modules/shared/record/RecordFieldKit";

const addedAgo = (value?: string | null) =>
  value
    ? `Added ${formatDistanceToNow(new Date(value), { addSuffix: true })}`
    : null;

/** Big editable title used by record pages and the side panel. */
const EditableTitle = ({
  value,
  placeholder,
  onSave,
  fields,
}: {
  value: string;
  placeholder: string;
  /** One input per field (e.g. first and last name). */
  fields: Array<{ key: string; value: string; placeholder: string }>;
  onSave: (values: Record<string, string>) => Promise<boolean>;
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!editing)
      setDraft(
        Object.fromEntries(fields.map((field) => [field.key, field.value])),
      );
  }, [editing, fields]);

  const commit = async () => {
    const changed = fields.some(
      (field) => (draft[field.key] ?? "").trim() !== field.value,
    );
    if (!changed || (await onSave(draft))) setEditing(false);
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter") void commit();
    if (event.key === "Escape") setEditing(false);
  };

  if (editing) {
    return (
      <div
        className="flex w-full gap-1.5"
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node))
            void commit();
        }}
      >
        {fields.map((field, index) => (
          <Input
            key={field.key}
            autoFocus={index === 0}
            value={draft[field.key] ?? ""}
            placeholder={field.placeholder}
            className="h-9 text-center text-base font-semibold"
            onChange={(event) =>
              setDraft({ ...draft, [field.key]: event.target.value })
            }
            onKeyDown={onKeyDown}
          />
        ))}
      </div>
    );
  }

  return (
    <RowButton
      layout="none"
      density="none"
      className="max-w-full truncate rounded-md px-2 text-lg font-semibold hover:bg-muted/50"
      onClick={() => setEditing(true)}
    >
      {value || <span className="text-muted-foreground">{placeholder}</span>}
    </RowButton>
  );
};

export const CompanyIdentity = ({
  record,
  stats,
}: {
  record: Company;
  stats?: ReactNode;
}) => {
  const { save } = useInlineSave("companies", record);
  const fields = [
    { key: "name", value: record.name ?? "", placeholder: "Company name" },
  ];
  return (
    <div className="flex flex-col items-center gap-1.5 px-4 pt-5 pb-4 text-center">
      <CompanyAvatar record={record} width={40} height={40} />
      <EditableTitle
        value={record.name ?? ""}
        placeholder="Company name"
        fields={fields}
        onSave={(values) => save({ name: values.name.trim() })}
      />
      <div className="flex flex-wrap items-center justify-center gap-1.5 text-sm text-muted-foreground">
        {record.is_client ? (
          <Badge
            variant="outline"
            className="border-success/40 bg-success/10 text-2xs text-success-text"
          >
            Client
          </Badge>
        ) : null}
        {addedAgo(record.created_at)}
      </div>
      {stats}
      <ClientQuickActions
        record={record}
        primaryContactId={record.primary_contact_id}
        className="mt-2 justify-center"
      />
    </div>
  );
};

export const ContactIdentity = ({
  record,
  stats,
}: {
  record: Contact;
  stats?: ReactNode;
}) => {
  const { save } = useInlineSave("contacts", record);
  const fullName = [record.first_name, record.last_name]
    .filter(Boolean)
    .join(" ");
  const fields = [
    {
      key: "first_name",
      value: record.first_name ?? "",
      placeholder: "First name",
    },
    { key: "last_name", value: record.last_name ?? "", placeholder: "Last" },
  ];
  return (
    <div className="flex flex-col items-center gap-1.5 px-4 pt-5 pb-4 text-center">
      <Avatar record={record} width={40} height={40} />
      <EditableTitle
        value={fullName}
        placeholder="Name"
        fields={fields}
        onSave={(values) =>
          save({
            first_name: values.first_name.trim(),
            last_name: values.last_name.trim() || values.first_name.trim(),
          })
        }
      />
      <div className="text-sm text-muted-foreground">
        {[record.title, addedAgo(record.first_seen)]
          .filter(Boolean)
          .join(" · ")}
      </div>
      {stats}
      <ContactQuickActions
        contactId={record.id}
        contact={record}
        compact
        className="mt-2"
      />
    </div>
  );
};

const PersonChip = ({
  contact,
  primary,
  onOpen,
}: {
  contact: Contact;
  primary: boolean;
  onOpen?: (contact: Contact) => void;
}) => {
  const name =
    [contact.first_name, contact.last_name].filter(Boolean).join(" ") ||
    "Unnamed";
  const email = getContactPrimaryEmail(contact);
  const content = (
    <>
      <Avatar record={contact} width={20} height={20} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1 truncate font-medium">
          {name}
          {primary ? (
            <Star
              className="size-3 shrink-0 fill-warning text-warning-text"
              aria-label="Primary contact"
            />
          ) : null}
          {isLeadLifecycleStatus(contact.status) ? (
            <Badge
              variant="outline"
              className="ml-auto shrink-0 px-1.5 py-0 text-2xs capitalize"
            >
              {contact.lead_stage?.replace(/_/g, " ") || contact.status}
            </Badge>
          ) : null}
        </span>
        {email ? (
          <span className="block truncate text-xs text-muted-foreground">
            {email}
          </span>
        ) : null}
      </span>
    </>
  );
  const className =
    "flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-left text-sm hover:bg-accent";
  return onOpen ? (
    <Pressable className={className} onClick={() => onOpen(contact)}>
      {content}
    </Pressable>
  ) : (
    <Link to={getContactShowPath(contact.id)} className={className}>
      {content}
    </Link>
  );
};

const PersonList = ({
  people,
  onOpen,
}: {
  people: Contact[];
  onOpen?: (contact: Contact) => void;
}) => (
  <div className="space-y-0.5">
    {people.map((contact) => (
      <PersonChip
        key={contact.id}
        contact={contact}
        primary={false}
        onOpen={onOpen}
      />
    ))}
  </div>
);

const SectionIconLink = ({ to, label }: { to: string; label: string }) => (
  <IconButton className="size-7" aria-label={label} asChild>
    <Link to={to}>
      <ArrowUpRight className="size-3.5" />
    </Link>
  </IconButton>
);

/** People, leads and referrals of an account. */
export const CompanyRelatedSections = ({
  record,
  onOpenContact,
  onAddContact,
}: {
  record: Company;
  onOpenContact?: (contact: Contact) => void;
  onAddContact?: () => void;
}) => {
  const { data: people = [] } = useGetList<Contact>(
    "contacts",
    {
      filter: { "company_id@eq": record.id },
      pagination: { page: 1, perPage: 50 },
      sort: { field: "first_name", order: "ASC" },
    },
    { staleTime: 30_000 },
  );
  const { data: referrals = [] } = useGetList<Contact>(
    "contacts",
    {
      filter: { "referred_by_company_id@eq": record.id },
      pagination: { page: 1, perPage: 50 },
      sort: { field: "last_seen", order: "DESC" },
    },
    { staleTime: 30_000 },
  );
  const primaryId = record.primary_contact_id;
  const ordered = people
    .filter((contact) => !isLeadLifecycleStatus(contact.status))
    .sort(
      (a, b) =>
        Number(String(b.id) === String(primaryId)) -
        Number(String(a.id) === String(primaryId)),
    );
  const leads = people.filter((contact) =>
    isLeadLifecycleStatus(contact.status),
  );

  return (
    <>
      <RelatedSection
        title={`People${ordered.length ? ` · ${ordered.length}` : ""}`}
        action={
          onAddContact ? (
            <IconButton
              className="size-7"
              aria-label="Add contact"
              onClick={onAddContact}
            >
              <Plus className="size-3.5" />
            </IconButton>
          ) : null
        }
      >
        {ordered.length ? (
          <div className="space-y-0.5">
            {ordered.map((contact) => (
              <PersonChip
                key={contact.id}
                contact={contact}
                primary={String(contact.id) === String(primaryId)}
                onOpen={onOpenContact}
              />
            ))}
          </div>
        ) : (
          <p className="px-1.5 text-sm text-muted-foreground">No people yet.</p>
        )}
      </RelatedSection>
      {leads.length ? (
        <RelatedSection title={`Leads · ${leads.length}`}>
          <PersonList people={leads} onOpen={onOpenContact} />
        </RelatedSection>
      ) : null}
      {referrals.length ? (
        <RelatedSection title={`Referrals · ${referrals.length}`}>
          <PersonList people={referrals} onOpen={onOpenContact} />
        </RelatedSection>
      ) : null}
    </>
  );
};

/** Account and referrals of a person. */
export const ContactRelatedSections = ({ record }: { record: Contact }) => {
  const { data: companies = [] } = useGetList<Company>(
    "companies",
    {
      filter: { "id@eq": record.company_id ?? -1 },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "id", order: "ASC" },
    },
    { enabled: record.company_id != null, staleTime: 60_000 },
  );
  const company = companies[0];
  const { data: referrals = [] } = useGetList<Contact>(
    "contacts",
    {
      filter: { "referred_by_contact_id@eq": record.id },
      pagination: { page: 1, perPage: 50 },
      sort: { field: "last_seen", order: "DESC" },
    },
    { staleTime: 30_000 },
  );

  return (
    <>
      <RelatedSection
        title="Account"
        action={
          company ? (
            <SectionIconLink
              to={getClientShowPath(company.id)}
              label="Open account"
            />
          ) : null
        }
      >
        {company ? (
          <Link
            to={getClientShowPath(company.id)}
            className="flex items-center gap-2 rounded-md px-1.5 py-1 text-sm hover:bg-accent"
          >
            <CompanyAvatar record={company} width={20} height={20} />
            <span className="truncate font-medium">{company.name}</span>
            {String(company.primary_contact_id) === String(record.id) ? (
              <Star
                className="size-3 shrink-0 fill-warning text-warning-text"
                aria-label="Primary contact"
              />
            ) : null}
          </Link>
        ) : (
          <p className="px-1.5 text-sm text-muted-foreground">No account.</p>
        )}
      </RelatedSection>
      <ContactPortalAccessSection record={record} company={company} />
      {referrals.length ? (
        <RelatedSection title={`Referrals · ${referrals.length}`}>
          <PersonList people={referrals} />
        </RelatedSection>
      ) : null}
    </>
  );
};
